/* eslint-disable class-methods-use-this,react/prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import staticTimeoutResolver from '@dc/ui-library/src/helpers/staticTimeoutResolver';
import { postJSON } from '@dc/ui-library/src/helpers/network';
import { getQueryParameter, setQueryParameter } from '@dc/ui-library/src/helpers/urlManipulation';
import { resolveByCountry } from '@dc/ui-library/src/helpers/environment';
import { browser } from '@dc/ui-library/src/helpers/browser';
import UserSignInActionBuilder from '@dc/data-layer/src/UserSignInActionBuilder';
import brightworldTheme from '@codex-brightworld/themes';
import { Icon } from '@codex-brightworld/icons';
import { Button } from '@codex-brightworld/basics';
import QuickAccount from '@dc/ui-library/src/apps/QuickAccount/';
import Portal from '@dc/ui-library/src/gadgets/Portal';
import styled, { ThemeProvider } from 'styled-components';

const WISHLIST_ITEM_ADD = 128;
const WISHLIST_ITEM_REMOVE = 256;
const SUCCESS_STATUS = 'OK';
const ERROR_STATUS = 'ERROR';
const UNAUTHORIZED_STATUS = 'UNAUTHORIZED';
const MESSAGE_TYPE_INFO = 'info';
const MESSAGE_TYPE_POSITIVE = 'positive';
const MESSAGE_TYPE_NEGATIVE = 'negative';

const IconHeart = styled(Icon)`
  margin: -3px 7px 0px 0px;

  svg path:last-child {
    fill: ${(props) => props.theme.colors.primary.purple};
  }

  > svg {
    > g {
      /* Hide border of the icon */
      path:nth-child(1) {
        display: none
      }
    }
  }
`;

const ButtonSaveForLater = styled(Button)`
  width: 100%;
  border: none;
`;
export class SaveForLater extends Component {

  static handleEmailRecognitionResult(emailExists) {
    window.appState.dispatch({
      type: 'SFL_EMAIL_RECOGNITION',
      payload: {
        emailExists: emailExists,
        trackFunctionArgs: 'saveForLaterEmailRecognition'
      }
    });
  }

  static handleSignInResult(signInStatus, saveForLaterStatus, userMessage, rememberMeSelected) {
    window.appState.dispatch(UserSignInActionBuilder.quickAccountLogin(
      signInStatus,
      rememberMeSelected,
      saveForLaterStatus
    ));
  }

  static handleRegistrationResult(registerStatus, saveForLaterStatus) {
    window.appState.dispatch(UserSignInActionBuilder.quickAccountCreation(
      registerStatus,
      saveForLaterStatus
    ));
  }

  constructor(props) {
    super(props);

    let emailAddProductEncoded;

    if (props.enableAddProductByEmail) {
      emailAddProductEncoded = getQueryParameter(window.location, 'email', '') || undefined;
      window.history.replaceState(
        {},
        document.title,
        setQueryParameter(window.location, 'email', false)
      );
    }

    this.state = {
      userLoggedIn: props.userLoggedIn,
      itemSaved: props.itemSaved,
      inProgress: false,
      message: '',
      messageType: MESSAGE_TYPE_INFO,
      quickAccountPopupOpened: !!emailAddProductEncoded,
      emailAddProduct: emailAddProductEncoded ? atob(emailAddProductEncoded) : undefined
    };

    this.analyticsPayload = {};

    this.closeQuickAccount = this.closeQuickAccount.bind(this);
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleProductAdded = this.handleProductAdded.bind(this);
  }

  handleButtonClick(event) {
    const { props, state } = this;

    if (event) {
      event.preventDefault();
    }

    if (state.inProgress) {
      return;
    }

    if (!props.userLoggedIn) {
      this.initQuickAccount();
      return;
    }

    this.toggleProduct(
      state.itemSaved ? WISHLIST_ITEM_REMOVE : WISHLIST_ITEM_ADD,
      props.productId
    );
  }

  handleSuccessResponse(response, bitmask) {
    switch (response.status) {
      case SUCCESS_STATUS: {
        this.analyticsPayload.result = 'success';
        this.setSuccessMessage(bitmask);
        break;
      }
      case ERROR_STATUS: {
        this.analyticsPayload.result = 'fail';
        this.setErrorMessage(response.formSubmitFeedback[bitmask].formMessages);
        break;
      }
      case UNAUTHORIZED_STATUS: {
        this.initQuickAccount();
        break;
      }
      default: {
        this.analyticsPayload.result = 'fail';
        this.setErrorMessage([
          bitmask === WISHLIST_ITEM_ADD ? 'saving_error' : 'removing_error'
        ]);
        break;
      }
    }
  }

  handleProductAdded() {
    this.setState({ itemSaved: true });
  }


  setSuccessMessage(bitmask) {
    const message = window.App.locale.getString('component.save-for-later.saving_success');
    this.analyticsPayload.resultMessage = message;

    if (bitmask === WISHLIST_ITEM_ADD) {
      this.setState({ itemSaved: true, message, messageType: MESSAGE_TYPE_POSITIVE });

    } else if (bitmask === WISHLIST_ITEM_REMOVE) {
      this.setState({ itemSaved: false });
    }

    setTimeout(() => {
      this.setState({ message: '' });
    }, staticTimeoutResolver(message));
  }

  setErrorMessage(messages) {
    const message = this.extractMessages(
      messages,
      { maxProductsCount: window.App.settings.get('component.save-for-later.max-products-count') }
    );
    this.analyticsPayload.resultMessage = message;
    this.analyticsPayload.result = 'fail';

    this.setState({ message, messageType: MESSAGE_TYPE_NEGATIVE });

    setTimeout(() => {
      this.setState({ message: '' });
    }, staticTimeoutResolver(message));
  }

  setProgress(inProgress) {
    this.setState({ inProgress });
  }

  getProductInfo() {
    if (this.props.productInfo) {
      return this.props.productInfo;
    }
    const appState = window.appState ? window.appState.getState() : {};
    const productInfo = appState && appState.productInfo ? appState.productInfo : {};
    const currency = resolveByCountry('£', '€');

    return {
      productId: productInfo.productID,
      name: `${productInfo.manufacturer} ${productInfo.productName}`,
      image: productInfo.imageUrl,
      price: productInfo.currentPrice ? `${currency}${productInfo.currentPrice.toFixed(2)}` : '',
      savePrice: productInfo.savingsPrice
        ? `Save ${currency}${productInfo.savingsPrice.toFixed(2)}`
        : '',
      wasPrice: productInfo.wasPrice
        ? `Was ${currency}${productInfo.wasPrice.toFixed(2)}
          (from ${productInfo.wasPriceFrom} to ${productInfo.wasPriceTo})`
        : '',
      isOutOfStock: !productInfo.isInStock,
      rating: productInfo.reviewRating,
      ratingCount: productInfo.numberOfReviews
    };
  }

  closeQuickAccount(event) {
    if (event) {
      event.preventDefault();
    }

    this.setState({ quickAccountPopupOpened: false });
  }

  initQuickAccount() {
    this.setState({ quickAccountPopupOpened: true });
  }

  toggleProduct(bitmask, productId) {
    this.setProgress(true);

    postJSON(bitmask, { wishlistId: 0, productId })
      .then((response) => {
        this.setProgress(false);
        this.handleSuccessResponse(response, bitmask);
        this.dispatchAnalytics(bitmask);
      })
      .catch(() => {
        this.setProgress(false);
        this.setErrorMessage([
          bitmask === WISHLIST_ITEM_ADD ? 'saving_error' : 'removing_error'
        ]);
        this.dispatchAnalytics(bitmask);
      });
  }

  dispatchAnalytics(bitmask) {
    const removing = bitmask === WISHLIST_ITEM_REMOVE;

    window.appState.dispatch({
      type: 'SAVE_FOR_LATER',
      payload: {
        addOrRemove: removing
          ? 'Removed'
          : 'Added',
        result: this.analyticsPayload.result,
        resultMessage: removing
          ? null
          : this.analyticsPayload.resultMessage,
        trackFunctionArgs: 'saveForLater'
      }
    });
  }

  extractMessages(messages, params) {
    const outputMessages = messages.map((message) => {
      let translation = window.App.locale.getString(`component.save-for-later.${message}`);

      Object.keys(params).forEach((key) => {
        translation = translation.replace(`[${key}]`, params[key]);
      });

      return translation;
    });

    return outputMessages.join(' ');
  }

  renderTooltip() {
    const { state } = this;
    let tooltip = '';

    if (state.message) {
      tooltip = (
        <span className={'dc-sfl-tooltip'}>
          {state.messageType === MESSAGE_TYPE_POSITIVE &&
            <Icon icon={'tick'} />}
          <span dangerouslySetInnerHTML={{ __html: state.message }} />
        </span>
      );
    }

    return tooltip;
  }

  render() {
    const { props, state } = this;
    const isSaved = state.itemSaved;
    const classes = ['dc-sfl', isSaved ? 'dc-sfl-saved' : 'dc-sfl-removed', 'ab-test-hidden'];
    const buttonText = isSaved
      ? window.App.locale.getString('component.save-for-later.item_saved')
      : window.App.locale.getString('component.save-for-later.save_for_later');

    return (
      <ThemeProvider theme={brightworldTheme.brightworld}>
        <div
          className={classes.join(' ')}
          data-qa={`save-for-later-for-${props.qaDeviceType}`}
        >

          <ButtonSaveForLater
            qa={`save-for-later-button-for-${props.qaDeviceType}`}
            dataInteraction={'save-for-later-product'}
            type={'button'}
            tooltip={this.renderTooltip()}
            tooltipType={state.messageType}
            onClick={this.handleButtonClick}
            classes={'dc-sfl-label-width'}
            fadeInOut
          >

            <IconHeart icon={'heart'} />
            {buttonText}
          </ButtonSaveForLater>

          {state.quickAccountPopupOpened && (
          <Portal domNode={document.body}>
            <QuickAccount
              product={this.getProductInfo()}
              onPopupClose={this.closeQuickAccount}
              onEmailRecognitionResult={SaveForLater.handleEmailRecognitionResult}
              onSignInResult={SaveForLater.handleSignInResult}
              onRegistrationResult={SaveForLater.handleRegistrationResult}
              emailAddProduct={state.emailAddProduct}
              customerEmail={props.userLoggedIn ? props.customerEmail : undefined}
              onProductAdded={this.handleProductAdded}
            />
          </Portal>
          )}
        </div>
      </ThemeProvider>
    );
  }
}

SaveForLater.propTypes = {
  qaDeviceType: PropTypes.oneOf(['mobiles', 'desktop']).isRequired,
  enableAddProductByEmail: PropTypes.bool,
  itemSaved: PropTypes.bool.isRequired,
  customerEmail: PropTypes.string,
  userLoggedIn: PropTypes.bool.isRequired,
  productInfo: PropTypes.shape({
    productId: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
    price: PropTypes.string,
    savePrice: PropTypes.string,
    wasPrice: PropTypes.string,
    isOutOfStock: PropTypes.bool,
    rating: PropTypes.number,
    ratingCount: PropTypes.number
  })
};

SaveForLater.defaultProps = {
  productInfo: null,
  enableAddProductByEmail: false,
  customerEmail: undefined
};

function getInitData(element, enableAddProductByEmail) {
  const qaDeviceType = element.closest('#product-actions-touch')
    ? 'mobiles'
    : 'desktop';

  const groupcallWishlist = window.App.settings.get('wishlist') || {};
  const groupcallUserDetails = window.App.settings.get('userDetails') || {};

  const initData = {
    qaDeviceType: qaDeviceType,
    customerEmail: groupcallUserDetails ? groupcallUserDetails.email : undefined,
    userLoggedIn: groupcallWishlist.isLogged === true,
    itemSaved: groupcallWishlist.productSaved === true,
    productId: element.getAttribute('data-product-id'),
    enableAddProductByEmail
  };

  return initData;
}

export function init() {
  const saveForLaterRoot = document.querySelectorAll('[data-component="SaveForLater"]');
  const isTouch = browser().isTouch;

  if (saveForLaterRoot.length) {
    Array.prototype.slice.call(saveForLaterRoot).forEach((element, i) => {
      ReactDOM.render(
        React.createElement(SaveForLater, getInitData(element, i === (isTouch ? 0 : 1))),
        element
      );
    });
  }
}
